<template>
  <v-dialog v-model="visible" scrollable persistent max-width="1500px">
    <v-card>
      <!-- Header -->
      <v-card-title class="primary white--text py-3 pr-3">
        <v-icon left color="white">mdi-car-multiple</v-icon>
        Nuevo Movimiento Múltiple
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="max-height: 75vh; overflow-y: auto; overflow-x: auto;">
        <v-form ref="form" v-model="isValid">
          <div class="rows-container">
            <!-- Cabecera de columnas -->
            <v-row class="column-headers grey lighten-4 rounded mb-2 mx-0 py-2" no-gutters>
              <v-col cols="auto" style="width:36px;" class="text-center d-flex align-center justify-center">
                <span class="caption font-weight-bold grey--text text--darken-2">#</span>
              </v-col>
              <v-col cols="2" class="px-1 d-flex align-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Vehículo (matrícula / bastidor / IIC)</span>
              </v-col>
              <v-col cols="1" class="px-1 d-flex align-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Transporte</span>
              </v-col>
              <v-col cols="2" class="px-1 d-flex align-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Conductor / Grúa</span>
              </v-col>
              <v-col class="px-1 d-flex align-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Ubicación origen</span>
              </v-col>
              <v-col class="px-1 d-flex align-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Ubicación destino</span>
              </v-col>
              <v-col class="px-1 d-flex align-center" style="min-width:200px; max-width:200px;">
                <span class="caption font-weight-bold grey--text text--darken-2">Fecha movimiento</span>
              </v-col>
              <v-col class="px-1 d-flex align-center" style="min-width:200px; max-width:200px;">
                <span class="caption font-weight-bold grey--text text--darken-2">Fecha devolución</span>
              </v-col>
              <v-col cols="auto" style="width:44px;" class="text-center d-flex align-center justify-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Info</span>
              </v-col>
              <v-col cols="1" class="text-center d-flex align-center justify-center">
                <span class="caption font-weight-bold grey--text text--darken-2">Borrar</span>
              </v-col>
            </v-row>

            <!-- Filas de datos -->
            <template v-for="(row, index) in rows">
              <v-row
                :key="row.id"
                class="movement-row mx-0 mb-2 rounded"
                :class="[
                  { 'grey lighten-5': index % 2 === 0 && !getRowBlockReason(row) },
                  { 'blocked-row': !!getRowBlockReason(row) }
                ]"
                align="center"
                no-gutters
              >
              <!-- Número de fila -->
              <v-col cols="auto" style="width:36px;" class="text-center py-2">
                <v-avatar size="24" color="primary" class="white--text caption">{{ index + 1 }}</v-avatar>
              </v-col>

              <!-- Vehículo — siempre habilitado, va primero -->
              <v-col cols="2" class="px-1 py-1">
                <v-autocomplete
                  v-model="row.vehicle"
                  :items="row.vehicleSearchResults"
                  :loading="row.vehicleSearchLoading"
                  :search-input.sync="row.vehicleSearch"
                  :filter="() => true"
                  :item-text="vehicleDisplayText"
                  return-object
                  dense
                  outlined
                  hide-details
                  hide-no-data
                  clearable
                  class="compact-field"
                  placeholder="Matrícula, bastidor o IIC..."
                  :rules="[v => !!v || 'Requerido']"
                  @input="onVehicleSelect(row, $event)"
                  @click:clear="onVehicleClear(row)"
                  @update:search-input="searchVehicles(row, $event)"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon small class="mr-1" color="primary">mdi-car</v-icon>
                        {{ item.brand }} {{ item.model }}
                      </v-list-item-title>
                      <v-list-item-title style="font-size:0.85em; font-weight:normal;">
                        {{ item.frame_number ? 'B: ' + (item.frame_number.length > 6 ? '...' + item.frame_number.slice(-6) : item.frame_number) : '' }}
                        {{ item.plate_number ? '  M: ' + item.plate_number : '' }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="item.dealer">
                        <v-icon x-small class="mr-1">mdi-store</v-icon>{{ item.dealer.name }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span class="caption font-weight-medium text-truncate" style="max-width:140px;">
                      {{ item.plate_number || item.frame_number }}
                      <span v-if="item.brand" class="grey--text"> · {{ item.brand }} {{ item.model }}</span>
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Método de transporte -->
              <v-col cols="1" class="px-1 py-1">
                <v-select
                  v-model="row.transportation"
                  :items="transportOptions"
                  :disabled="!row.vehicle || !!getRowBlockReason(row)"
                  dense
                  outlined
                  hide-details
                  class="compact-field"
                  :rules="[v => !!v || 'Requerido']"
                  @change="onTransportChange(row)"
                ></v-select>
              </v-col>

              <!-- Conductor / Grúa -->
              <v-col cols="2" class="px-1 py-0 driver-col" style="align-self: center;">
                <div style="width:100%;">
                <driver-selector-inline
                  v-if="row.vehicle && !getRowBlockReason(row) && row.transportation && row.transportation !== 'GRUA'"
                  :value="row.driver"
                  :current-dealer-id="currentDealerId"
                  @input="row.driver = $event; row.driver_id = $event ? $event.id : null"
                />
                <grua-selector-inline
                  v-else-if="row.vehicle && !getRowBlockReason(row) && row.transportation === 'GRUA'"
                  :value="row.grua"
                  :current-dealer-id="currentDealerId"
                  @input="row.grua = $event; row.grua_id = $event ? $event.id : null"
                />
                <v-text-field
                  v-else
                  dense
                  outlined
                  hide-details
                  disabled
                  :placeholder="getRowBlockReason(row) ? 'Bloqueado' : '—'"
                  class="compact-field"
                />
                </div>
              </v-col>

              <!-- Ubicación origen -->
              <v-col class="px-1 py-1">
                <v-autocomplete
                  v-model="row.source_location_id"
                  :items="locationItems"
                  :disabled="!row.vehicle || !!getRowBlockReason(row)"
                  item-value="id"
                  item-text="displayName"
                  dense
                  outlined
                  hide-details
                  class="compact-field"
                  clearable
                  :rules="[v => !!v || 'Requerido']"
                ></v-autocomplete>
              </v-col>

              <!-- Ubicación destino -->
              <v-col class="px-1 py-1">
                <v-autocomplete
                  v-model="row.destination_location_id"
                  :items="locationItems"
                  :disabled="!row.vehicle || !!getRowBlockReason(row)"
                  item-value="id"
                  item-text="displayName"
                  dense
                  outlined
                  hide-details
                  class="compact-field"
                  clearable
                  :rules="[v => !!v || 'Requerido']"
                ></v-autocomplete>
              </v-col>

              <!-- Fecha movimiento -->
              <v-col class="px-1 py-0" style="align-self: center; min-width:200px; max-width:200px;">
                <custom-date-time-picker
                  :value="row.datetime"
                  dense
                  label="Fecha"
                  :disabled="!row.vehicle || !!getRowBlockReason(row)"
                  :rules="[v => !!v || 'Requerido']"
                  :movements="getMovementsForPicker(row)"
                  :is-movement="true"
                  @input="row.datetime = $event"
                />
              </v-col>

              <!-- Fecha devolución -->
              <v-col class="px-1 py-0" style="align-self: center; min-width:200px; max-width:200px;">
                <custom-date-time-picker
                  :value="row.end_date"
                  label=""
                  dense
                  :min="row.datetime || undefined"
                  :disabled="!row.vehicle || !row.datetime || !!getRowBlockReason(row)"
                  :occupied-ranges="getOccupiedRanges(row)"
                  @input="row.end_date = $event"
                />
              </v-col>

              <!-- Info adicional por fila -->
              <v-col cols="auto" style="width:44px;" class="text-center py-1">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      :color="row.additional_info ? 'orange darken-1' : 'grey lighten-1'"
                      :disabled="!row.vehicle || !!getRowBlockReason(row)"
                      v-on="on"
                      @click="openAdditionalInfo(row)"
                    >
                      <v-icon small>{{ row.additional_info ? 'mdi-note-text' : 'mdi-note-plus-outline' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ row.additional_info ? 'Editar info adicional' : 'Añadir info adicional' }}</span>
                </v-tooltip>
              </v-col>

              <!-- Eliminar fila -->
              <v-col cols="1" class="text-center py-1">
                <v-btn
                  icon
                  small
                  color="error"
                  :disabled="rows.length <= 1"
                  @click="removeRow(index)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row
              v-if="getRowBlockReason(row)"
              :key="'block-' + row.id"
              class="mx-0 mb-2 mt-n2"
              no-gutters
            >
              <v-col cols="12" class="px-1">
                <v-alert dense type="warning" color="orange" class="caption mb-0 py-1" style="font-size:11px;">
                  <v-icon x-small left>mdi-alert-circle-outline</v-icon>
                  {{ getRowBlockReason(row) }}
                </v-alert>
              </v-col>
            </v-row>
            </template>
          </div>

          <!-- Botón añadir fila -->
          <v-btn text color="primary" small class="mt-3" @click="addRow">
            <v-icon left small>mdi-plus</v-icon>
            Añadir fila
          </v-btn>

          <!-- Alerta global si hay filas bloqueadas — mismas condiciones que VehiculoDetail.vue -->
          <v-alert v-if="blockedRows.length > 0" dense type="warning" color="orange" class="mt-4 caption mb-0">
            <v-icon small left>mdi-alert-circle</v-icon>
            {{ blockedRows.length }} vehículo(s) no pueden tener movimientos: revisa los avisos por fila.
            <span v-if="blockedRows.length === rows.filter(r=>r.vehicle).length"> — no se podrá crear ningún movimiento hasta corregir.</span>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Acciones -->
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text color="grey darken-1" :disabled="loading" @click="cancel">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="loading"
          :disabled="loading || blockedRows.length > 0"
          @click="submit"
        >
          <v-icon left>mdi-send</v-icon>
          Crear movimientos ({{ rows.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
    <!-- Diálogo: info adicional por movimiento -->
    <v-dialog v-model="additionalInfoEdit.visible" max-width="500" persistent>
      <v-card v-if="additionalInfoEdit.row">
        <v-card-title class="orange darken-1 white--text py-3">
          <v-icon left color="white">mdi-note-text</v-icon>
          Información Adicional
          <v-spacer></v-spacer>
          <v-btn icon dark @click="additionalInfoEdit.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="caption grey--text mb-3" v-if="additionalInfoEdit.row.vehicle">
            <v-icon x-small class="mr-1">mdi-car</v-icon>
            {{ additionalInfoEdit.row.vehicle.brand }} {{ additionalInfoEdit.row.vehicle.model }}
            <span v-if="additionalInfoEdit.row.vehicle.plate_number"> · {{ additionalInfoEdit.row.vehicle.plate_number }}</span>
          </div>
          <v-textarea
            v-model="additionalInfoEdit.text"
            label="Información adicional"
            placeholder="Escribe aquí cualquier nota o detalle sobre este movimiento..."
            outlined
            rows="5"
            auto-grow
            counter="500"
            maxlength="500"
            hide-details="auto"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn text color="error" small @click="clearAdditionalInfo">
            <v-icon left small>mdi-delete-outline</v-icon>
            Limpiar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="additionalInfoEdit.visible = false">Cancelar</v-btn>
          <v-btn color="orange darken-1" dark depressed @click="saveAdditionalInfo">
            <v-icon left small>mdi-check</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import DriverSelectorInline from './DriverSelectorInline.vue'
import GruaSelectorInline from './GruaSelectorInline.vue'
import CustomDateTimePicker from './CustomDateTimePicker.vue'
import LocationService from '@/services/LocationService'
import { locationDisplayName } from '@/utils'
import VehiculoService from '@/services/VehiculoService'
import MovimientoService from '@/services/MovimientoService'

let rowCounter = 0

function createEmptyRow () {
  return {
    id: ++rowCounter,
    // vehicle
    vehicle: null,
    vehicle_id: null,
    vehicleSearch: null,
    vehicleSearchResults: [],
    vehicleSearchLoading: false,
    // movement data
    movement_type: 'move',
    transportation: null,
    driver: null,
    driver_id: null,
    grua: null,
    grua_id: null,
    source_location_id: null,
    destination_location_id: null,
    datetime: null,
    end_date: null,
    additional_info: null
  }
}

export default {
  name: 'MultipleMovimientoForm',

  components: { DriverSelectorInline, GruaSelectorInline, CustomDateTimePicker },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    currentDealerId: {
      type: [String, Number],
      default: null
    },
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      visible: this.value,
      isValid: false,
      loading: false,
      locationItems: [],
      rows: [createEmptyRow(), createEmptyRow()],

      movementTypeItems: [
        { text: 'Movimiento', value: 'move' },
        { text: 'Cesión', value: 'assignment' }
      ],
      transportOptions: [
        { text: 'Driver', value: 'DRIVER' },
        { text: 'Grúa', value: 'GRUA' },
        { text: 'Yo mismo', value: 'YO MISMO' },
        { text: 'Cliente', value: 'CLIENTE' }
      ],
      // Cache de movimientos por vehicle_id → [{ datetime, end_date, status }]
      vehicleMovementsCache: {},

      // Diálogo de info adicional
      additionalInfoEdit: {
        visible: false,
        row: null,
        text: ''
      }
    }
  },

  watch: {
    value (val) {
      this.visible = val
      if (val) this.loadLocations()
    },
    visible (val) {
      this.$emit('input', val)
    }
  },

  computed: {
    blockedRows () {
      return this.rows.filter(row => !!this.getRowBlockReason(row))
    }
  },

  methods: {
    vehicleDisplayText (item) {
      if (!item) return ''
      let base = `${item.brand || ''} ${item.model || ''} - ${item.plate_number || item.frame_number || ''}`
      if (item.dealer) base += ` | ${item.dealer.name}`
      return base.trim()
    },

    // ── Validación misma que en VehiculoDetail.vue / VehicleDetailMovemnt.vue ──
    // Replica: isPending (status === 'pending'), isUnregister (last movement unreported),
    // canMov (status === 'registered', not free, has movements) y permisos.
    getVehicleBlockReason (vehicle) {
      if (!vehicle) return null
      // 1. Estado pending — mismo mensaje que VehicleDetailMovemnt.vue:36-38
      if (vehicle.status === 'pending') {
        return 'Complete todos los datos del vehículo antes de realizar un movimiento'
      }
      // 2. Estado free/archivado
      if (vehicle.status === 'free') {
        return 'Vehículo archivado — no se pueden crear movimientos'
      }
      // 3. Solo registrados pueden tener movimientos (VehiculoDetail.vue:733-734 canMov)
      if (vehicle.status === 'created') {
        return 'Debe recepcionar el vehículo antes de realizar un movimiento'
      }
      if (vehicle.status !== 'registered') {
        return 'Solo vehículos en estado registrado pueden tener movimientos'
      }
      // 4. Movimientos previos requeridos (canMov: vehicle.movements.length === 0)
      const movements = this.vehicleMovementsCache[vehicle.id] || vehicle.movements || []
      if (!movements || movements.length === 0) {
        return 'El vehículo no tiene movimientos previos'
      }
      // 5. Último movimiento pendiente de aceptar — mismo que isUnregister (VehicleDetailMovemnt.vue:479-484)
      const sorted = [...movements].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      const last = sorted[0]
      if (last && last.status === 'unreported') {
        return 'Debe aceptar el movimiento pendiente antes de realizar uno nuevo'
      }
      // 6. Permisos — replica canMov de VehiculoDetail.vue:735-744
      if (!this.hasMovementPermission(vehicle)) {
        return 'Sin permisos para crear movimientos en este vehículo'
      }
      return null
    },

    getRowBlockReason (row) {
      if (!row.vehicle) return null
      return this.getVehicleBlockReason(row.vehicle)
    },

    hasMovementPermission (vehicle) {
      try {
        // Colaborador puede crear sin restricción de dealer (VehiculoDetail.vue:737)
        if (this.$store && this.$store.getters.HAS_ROLE && this.$store.getters.HAS_ROLE(['colaborator'])) return true
        const dealerId = vehicle.dealer?.id || vehicle.dealer_id
        const myDealerId = this.$store?.getters?.CURRENT_USER?.dealer?.id || this.currentDealerId
        if (!dealerId) return true // si no hay dealer no bloqueamos por permisos
        if (dealerId === myDealerId) return true
        if (dealerId !== myDealerId && this.$store?.getters?.IS_PARTNER) {
          if (this.$store.getters.HAS_PARTNER_PERMISSION) {
            return this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'crear_movimiento', dealerId })
          }
          return true
        }
        // Fallback: si tiene permiso global crear_movimiento
        if (this.$store?.getters?.HAS_PERMISSION && this.$store.getters.HAS_PERMISSION('crear_movimiento')) return true
        // Si no se puede determinar, no bloquear por permisos (para no romper flujo múltiple)
        // pero mantenemos la estructura para replica fiel; descomenta siguiente línea para bloquear estricto:
        // return false
        return true
      } catch (e) {
        return true
      }
    },

    async onVehicleSelect (row, vehicle) {
      if (vehicle) {
        row.vehicle_id = vehicle.id
        // El endpoint de búsqueda puede devolver movements incompletos.
        // Hacemos view para obtener los movimientos/status reales (igual que antes).
        if (!this.vehicleMovementsCache[vehicle.id]) {
          try {
            const { data } = await VehiculoService.view({ id: vehicle.id })
            const fresh = data?.data || {}
            const movements = fresh.movements || []
            this.$set(this.vehicleMovementsCache, vehicle.id, movements)
            // Sincronizar vehicle del row con datos frescos para que getVehicleBlockReason vea status/movements actualizados
            Object.assign(row.vehicle, {
              status: fresh.status,
              movements: movements,
              dealer: fresh.dealer || row.vehicle.dealer,
              dealer_id: fresh.dealer_id || row.vehicle.dealer_id,
              location_id: fresh.location_id
            })
            if (fresh.location_id) {
              row.source_location_id = fresh.location_id
            }
          } catch (e) {
            console.warn('[VehicleSelect] error fetching vehicle detail:', e)
            this.$set(this.vehicleMovementsCache, vehicle.id, vehicle.movements || [])
          }
        } else {
          // Ya en cache: asegurar que row.vehicle tenga los movements del cache para la validación
          if (row.vehicle && (!row.vehicle.movements || row.vehicle.movements.length === 0)) {
            this.$set(row.vehicle, 'movements', this.vehicleMovementsCache[vehicle.id])
          }
        }
      } else {
        this.onVehicleClear(row)
      }
    },

    onVehicleClear (row) {
      row.vehicle = null
      row.vehicle_id = null
      row.vehicleSearch = null
      row.vehicleSearchResults = []
      row.movement_type = 'move'
      row.transportation = null
      row.driver = null
      row.driver_id = null
      row.grua = null
      row.grua_id = null
      row.source_location_id = null
      row.destination_location_id = null
      row.datetime = null
      row.end_date = null
      row.additional_info = null
    },

    // Devuelve array de { start, end } para un row, combinando cache + otras filas del form
    // Se mantiene por retrocompatibilidad (DateTimePickerInline). Para CustomDateTimePicker usar getMovementsForPicker
    getOccupiedRanges (currentRow) {
      if (!currentRow.vehicle_id) return []
      const vid = currentRow.vehicle_id
      const ranges = []

      // 1. Movimientos existentes del vehículo (vienen del expand=movements)
      const cached = this.vehicleMovementsCache[vid] || []
      for (const m of cached) {
        if (m.status !== 'completed' && (m.type === 'assignment' || m.type === 'move') && m.datetime) {
          ranges.push({ start: m.datetime, end: m.end_date || null })
        }
      }

      // 2. Otras filas del formulario con el mismo vehículo que ya tienen fecha
      for (const row of this.rows) {
        if (row === currentRow) continue
        if (row.vehicle_id === vid && row.datetime) {
          ranges.push({ start: row.datetime, end: row.end_date || null })
        }
      }

      return ranges
    },

    // Para CustomDateTimePicker: devuelve array de movements compatible con su prop `movements`
    getMovementsForPicker (currentRow) {
      if (!currentRow.vehicle_id) return []
      const vid = currentRow.vehicle_id
      const movements = []

      // 1. Movimientos cacheados del vehículo (formato que ya espera CustomDateTimePicker)
      const cached = this.vehicleMovementsCache[vid] || []
      for (const m of cached) {
        movements.push(m)
      }

      // 2. Otras filas del formulario con el mismo vehículo — se convierten a pseudo-movimientos para bloquear solapamiento intra-form
      for (const row of this.rows) {
        if (row === currentRow) continue
        if (row.vehicle_id === vid && row.datetime) {
          movements.push({
            datetime: row.datetime,
            end_date: row.end_date || null,
            status: 'unreported',
            type: row.movement_type || 'move'
          })
        }
      }

      return movements
    },

    searchVehicles: _.debounce(async function (row, search) {
      if (!search || search.length < 2) {
        row.vehicleSearchResults = []
        return
      }
      row.vehicleSearchLoading = true
      try {
        const { data } = await VehiculoService.get({ 'filter[search]': search })
        row.vehicleSearchResults = data?.data || []
      } catch (e) {
        row.vehicleSearchResults = []
      } finally {
        row.vehicleSearchLoading = false
      }
    }, 350),

    onTransportChange (row) {
      row.driver = null
      row.driver_id = null
      row.grua = null
      row.grua_id = null
    },

    async loadLocations () {
      try {
        const isPartner = this.$store.getters.IS_PARTNER
        const ownDealer = this.$store.getters.CURRENT_USER && this.$store.getters.CURRENT_USER.dealer
        if (isPartner) {
          const response = await LocationService.getPartnerLocations()
          const raw = response.data?.data?.locations || []
          this.locationItems = raw.map(loc => ({
            ...loc,
            displayName: locationDisplayName(loc, ownDealer)
          }))
        } else {
          const response = await LocationService.get({ 'per-page': 1000, expand: 'dealer,dealers' })
          const raw = response.data?.data || []
          this.locationItems = raw.map(loc => ({
            ...loc,
            displayName: locationDisplayName(loc, ownDealer)
          }))
        }
      } catch (e) {
        this.$toast && this.$toast.error('Error al cargar ubicaciones')
      }
    },

    addRow () {
      this.rows.push(createEmptyRow())
    },

    removeRow (index) {
      if (this.rows.length > 1) this.rows.splice(index, 1)
    },

    openAdditionalInfo (row) {
      this.additionalInfoEdit.row = row
      this.additionalInfoEdit.text = row.additional_info || ''
      this.additionalInfoEdit.visible = true
    },

    saveAdditionalInfo () {
      this.additionalInfoEdit.row.additional_info = this.additionalInfoEdit.text.trim() || null
      this.additionalInfoEdit.visible = false
    },

    clearAdditionalInfo () {
      this.additionalInfoEdit.text = ''
      this.additionalInfoEdit.row.additional_info = null
      this.additionalInfoEdit.visible = false
    },

    cancel () {
      this.visible = false
      this.rows = [createEmptyRow(), createEmptyRow()]
    },

    async submit () {
      if (!this.$refs.form.validate()) return

      // ── Validación previa con las mismas condiciones que VehiculoDetail.vue ──
      const blocked = this.rows
        .map((row, idx) => ({ row, idx, reason: this.getRowBlockReason(row) }))
        .filter(x => x.reason)
      if (blocked.length > 0) {
        const details = blocked.map(b => `Fila ${b.idx + 1} (${b.row.vehicle?.plate_number || b.row.vehicle?.frame_number || 'vehículo'}): ${b.reason}`).join('\n')
        this.$toast && this.$toast.error(`No se pueden crear movimientos:\n${details}`, { duration: 6000 })
        return
      }

      this.loading = true
      const errors = []

      for (const row of this.rows) {
        try {
          const datetimeTs = row.datetime || null

          await MovimientoService.post({
            vehicle_id: row.vehicle_id,
            type: row.movement_type,
            status: 'unreported',
            responsible_id: this.currentUser?.id || null,
            transportation: row.transportation,
            driver_id: row.driver_id || null,
            grua_id: row.grua_id || null,
            source_location_id: row.source_location_id,
            destination_location_id: row.destination_location_id,
            datetime: datetimeTs,
            end_date: row.end_date || null,
            additional_info: row.additional_info || null
          })
        } catch (e) {
          errors.push(e)
        }
      }

      this.loading = false

      if (errors.length === 0) {
        this.$toast && this.$toast.success(`${this.rows.length} movimientos creados correctamente`)
        this.$emit('save')
        this.cancel()
      } else if (errors.length < this.rows.length) {
        this.$toast && this.$toast.warning(`${this.rows.length - errors.length} creados, ${errors.length} con errores`)
        this.$emit('save')
        this.cancel()
      } else {
        this.$toast && this.$toast.error('Error al crear los movimientos')
      }
    }
  }
}
</script>

<style scoped>
.column-headers {
  border-bottom: 2px solid rgba(0, 0, 0, 0.12);
}

.movement-row {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s;
}

.movement-row:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

.blocked-row {
  border: 1px solid rgba(255, 152, 0, 0.45) !important;
  background-color: rgba(255, 152, 0, 0.08) !important;
}
.blocked-row:hover {
  background-color: rgba(255, 152, 0, 0.14) !important;
}

.compact-field ::v-deep .v-input__control {
  min-height: 36px !important;
}

.compact-field ::v-deep .v-input__slot {
  min-height: 36px !important;
  padding: 0 8px !important;
}

.compact-field ::v-deep input {
  font-size: 13px;
}

.rows-container {
  min-width: 1600px;
}

/* Evitar que driver-selector y grua-selector rompan la alineación de fila */
.movement-row ::v-deep .v-input {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

/* Forzar altura del input dentro de driver/grua inline igual al resto de campos */
.driver-col ::v-deep .v-input__slot {
  min-height: 36px !important;
  max-height: 36px !important;
  height: 36px !important;
  padding: 0 8px !important;
  overflow: hidden;
}

.driver-col ::v-deep .v-input__control {
  min-height: 36px !important;
}

.driver-col ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
  max-height: 32px;
  align-items: center;
}

.driver-col ::v-deep input {
  font-size: 13px;
  max-height: 30px;
}

/* CustomDateTimePicker inline: forzar misma altura compacta que el resto de campos */
.movement-row ::v-deep .custom-date-picker .v-input__slot {
  min-height: 36px !important;
  max-height: 36px !important;
  height: 36px !important;
  padding: 0 8px !important;
}
.movement-row ::v-deep .custom-date-picker .v-input__control {
  min-height: 36px !important;
}
.movement-row ::v-deep .custom-date-picker input {
  font-size: 13px;
}
.movement-row ::v-deep .custom-date-picker .v-text-field__details {
  display: none;
}
</style>
